import { useEffect, useLayoutEffect, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Alert, Card, EmptyState } from '@playerbase/ui';

import {
  FIELD_CAP,
  type BuilderError,
  type BuilderFieldKind,
  type UseFormBuilderState,
} from '../../../games/use-form-builder-state.js';
import { FieldRow, FieldRowOverlay } from './field-row.js';
import { FieldSidePanel } from './field-side-panel.js';

const ERROR_COPY: Record<BuilderError, string> = {
  field_cap_reached: `You have reached the ${FIELD_CAP}-field limit. Remove a field before adding another.`,
  select_options_min_one: 'A select field needs at least one option.',
  select_options_max_25: 'A select field can have at most 25 options.',
  nps_already_present: 'Only one NPS field per form.',
};

interface FieldsCardProps {
  builder: UseFormBuilderState;
}

export function FieldsCard({ builder }: FieldsCardProps): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Two-phase signal so a fresh add gets a smooth scroll AND a brief
  // accent flash. scrollTargetId clears after one render (it's only
  // needed long enough to fire scrollIntoView). flashTargetId stays set
  // for the animation duration; while it's present the matching row
  // renders with the inline accent halo. We clear it via timer so the
  // row settles back to its resting state.
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null);
  const [flashTargetId, setFlashTargetId] = useState<string | null>(null);
  // Tracks which field is currently being dragged so we can render a
  // floating copy in <DragOverlay>. Without the overlay, dnd-kit moves
  // the source row's DOM directly and rows of differing heights make
  // the dragged element visually stretch as siblings rearrange.
  const [activeId, setActiveId] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (scrollTargetId === null) return;
    const el = document.querySelector(
      `[data-field-id="${CSS.escape(scrollTargetId)}"]`,
    );
    if (el !== null) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setScrollTargetId(null);
  }, [scrollTargetId]);

  useEffect(() => {
    if (flashTargetId === null) return undefined;
    const handle = window.setTimeout(() => setFlashTargetId(null), 1200);
    return () => window.clearTimeout(handle);
  }, [flashTargetId]);

  function handleDragStart(event: DragStartEvent): void {
    setActiveId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent): void {
    setActiveId(null);
    const { active, over } = event;
    if (over === null || active.id === over.id) return;
    const fromIndex = builder.state.fields.findIndex((f) => f.clientId === active.id);
    const toIndex = builder.state.fields.findIndex((f) => f.clientId === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    builder.reorderFields(fromIndex, toIndex);
  }

  function handleDragCancel(): void {
    setActiveId(null);
  }

  function handleAddField(kind: BuilderFieldKind): void {
    const clientId = `client-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    builder.addField(kind, clientId);
    setScrollTargetId(clientId);
    setFlashTargetId(clientId);
  }

  const fields = builder.state.fields;

  return (
    <Card>
      <Card.Header>
        <Card.Title>Fields</Card.Title>
        <Card.Description>
          Up to {FIELD_CAP} fields. Drag the handle to reorder. Discord modals
          show fields in this order.
        </Card.Description>
      </Card.Header>
      <Card.Content>
        {builder.state.lastError !== null ? (
          <div className="mb-4">
            <Alert tone="warning">{ERROR_COPY[builder.state.lastError]}</Alert>
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-[16rem_1fr]">
          <FieldSidePanel
            fieldCount={fields.length}
            hasNps={fields.some((f) => f.kind === 'nps')}
            onAdd={handleAddField}
          />

          <div className="min-w-0">
            {fields.length === 0 ? (
              <div className="rounded-md border border-dashed border-border bg-surface p-6">
                <EmptyState
                  title="No fields yet"
                  description="Pick a field type from the panel to get started."
                />
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <SortableContext
                  items={fields.map((f) => f.clientId)}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="flex flex-col gap-3">
                    {fields.map((field) => (
                      <FieldRow
                        key={field.clientId}
                        field={field}
                        builder={builder}
                        flashing={field.clientId === flashTargetId}
                      />
                    ))}
                  </ul>
                </SortableContext>
                <DragOverlay>
                  {activeId !== null
                    ? (() => {
                        const active = fields.find((f) => f.clientId === activeId);
                        if (active === undefined) return null;
                        return <FieldRowOverlay field={active} builder={builder} />;
                      })()
                    : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>
      </Card.Content>
    </Card>
  );
}
